import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Home, Wallet, MessageCircle, User, FileText, Shield,
  Bell, Search, Bookmark, LogOut, Settings, Lock,
  AlertCircle, Briefcase, FileCheck, Menu, X, ChevronRight,
  Globe, User2,
  Settings2, ChartAreaIcon, Star, UserCheck
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import { Avatar } from "../components/ui/avatar";
import PolicyModals from "../components/ui/PolicyModals";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { MdReviews } from "react-icons/md";
import AdminHeader from "../Headers/AdminHeader";
import { useAppDispatch, getFavoriteByUserId } from "../redux";
import FavoriteButton from "../components/ui/FavoriteButton";
import ReviewModal from "../components/ui/ReviewModal";
import SocialMediaLinks from "../components/ui/SocialMediaLinks";
import { RoleEnum } from "../modules/shared/model/role.enum";

// RealTimeFavoriteButton component moved outside to prevent recreation
interface RealTimeFavoriteButtonProps {
  user: any;
  currentUserFavorite: any;
}

const RealTimeFavoriteButton = React.memo(({ user, currentUserFavorite }: RealTimeFavoriteButtonProps) => {
  const [isFavorite, setIsFavorite] = useState(user.favorite?.id ? true : false);
  
  // Listen for changes in currentUserFavorite to update the button state
  useEffect(() => {
    if (currentUserFavorite && currentUserFavorite.userIds) {
      const isFavorited = currentUserFavorite.userIds.some(favUser => favUser.id === user.id.toString());
      setIsFavorite(isFavorited);
    } else if (currentUserFavorite === null) {
      // If no favorites exist, set to false
      setIsFavorite(false);
    }
  }, [currentUserFavorite, user.id]);

  return (
    <FavoriteButton
      targetUserId={user.id.toString()}
      size="sm"
      favoriteData={{
        id: user.favorite?.id,
        isFavorite: isFavorite
      }}
    />
  );
});

RealTimeFavoriteButton.displayName = "RealTimeFavoriteButton";

const Favorites2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showProfessionalTermsModal, setShowProfessionalTermsModal] = useState(false);
  const [activeTab, setActiveTab] = useState("professionals");

  // Redux state
  const { user, isAuthenticated } = useSelector((state: any) => state.auth);
  const { currentUserFavorite, loading, error } = useSelector((state: any) => state.favorites);
  const dispatch = useAppDispatch();

  // Local state for processed data
  const [favoriteProfessionals, setFavoriteProfessionals] = useState([]);
  const [favoriteClients, setFavoriteClients] = useState([]);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProfessionalForReview, setSelectedProfessionalForReview] = useState(null);
  const [selectedClientForReview, setSelectedClientForReview] = useState(null);
  const lastFetchedUserIds = useRef<string[]>([]);

  console.log(currentUserFavorite);


  // StarRatingDisplay component for showing user ratings
  const StarRatingDisplay = ({ rating, count, size = "sm" }: { rating: number; count: number; size?: "sm" | "md" | "lg" }) => {
    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6"
    };

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-1">
        <div className="flex">
          {[...Array(fullStars)].map((_, i) => (
            <Star key={i} className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
          ))}
          {hasHalfStar && (
            <div className="relative">
              <Star className={`${sizeClasses[size]} text-gray-300`} />
              <div className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
                <Star className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
              </div>
            </div>
          )}
          {[...Array(emptyStars)].map((_, i) => (
            <Star key={i + fullStars + (hasHalfStar ? 1 : 0)} className={`${sizeClasses[size]} text-gray-300`} />
          ))}
        </div>
        <span className="text-sm font-medium text-blue-700">
          {rating.toFixed(1)} ({count} reviews)
        </span>
      </div>
    );
  };

  // Load favorites when component mounts
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      dispatch(getFavoriteByUserId(user.id));
    }
  }, [user?.id, isAuthenticated, dispatch]);

  // Process favorites data when it's loaded
  useEffect(() => {
    if (isAuthenticated && currentUserFavorite?.userIds) {
      const userIds = currentUserFavorite.userIds;

      // Only process if userIds have actually changed
      const userIdsString = userIds.map((u: any) => u.id).sort().join(",");
      const lastFetchedString = lastFetchedUserIds.current.sort().join(",");

      if (userIdsString !== lastFetchedString) {
        lastFetchedUserIds.current = userIds.map((u: any) => u.id);

        // Process professionals data directly in useEffect
        setProfessionalsLoading(true);
        try {
          const professionalUsers = userIds.filter(user => {
            const roleId = Number(user.role?.id);
            const isProfessional = roleId === RoleEnum.professional ||
              roleId === RoleEnum.internationl_professional ||
              roleId === RoleEnum.emergency_professional;
            return isProfessional;
          });

          const professionalData = professionalUsers.map(user => ({
            id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            avatar: user.photo?.path || `https://i.pravatar.cc/150?img=${user.id}`,
            profession: user.title || "Professional",
            rating: user.averageRating || 0,
            totalJobs: user.completedJobs || 0,
            location: user.address || "No location",
            isOnline: true,
            tags: user.skills || ["General Services"],
            description: user.bio || "Professional service provider",
            hourlyRate: user.hourlyRate || "$50",
            languages: user.languages || ["English"],
            verified: user.verified || false,
            backgroundChecked: user.backgroundChecked || false,
            reviewsCount: user.reviewsCount || 0,
            reviewsList: user.reviewsList || []
          }));
          setFavoriteProfessionals(professionalData);
        } catch (error) {
          setFavoriteProfessionals([]);
        } finally {
          setProfessionalsLoading(false);
        }

        // Process clients data directly in useEffect
        setClientsLoading(true);
        try {
          const clientUsers = userIds.filter(user => {
            const roleId = Number(user.role?.id);
            const isClient = roleId === RoleEnum.client || roleId === RoleEnum.internationl_client;
            return isClient;
          });

          const clientData = clientUsers.map(user => ({
            id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            avatar: user.photo?.path || `https://i.pravatar.cc/150?img=${user.id}`,
            industry: user.company?.industry?.name || "Not specified",
            totalProjects: user.company?.totalProjects || 0,
            location: user.address || "No location",
            isActive: user.status?.id === 1,
            projectTypes: user.company?.projectTypes || ["General Services"],
            company: user.company,
            rating: user.averageRating || 0,
            reviewsCount: user.reviewsCount || 0,
            reviewsList: user.reviewsList || []
          }));
          setFavoriteClients(clientData);
        } catch (error) {
          setFavoriteClients([]);
        } finally {
          setClientsLoading(false);
        }
      }
    } else if (isAuthenticated && !currentUserFavorite?.userIds) {
      // No favorites found for current user
      setFavoriteProfessionals([]);
      setFavoriteClients([]);
    } else if (!isAuthenticated) {
      // For unauthenticated users, we can't get full user data, so show empty
      setFavoriteProfessionals([]);
      setFavoriteClients([]);
    }
  }, [isAuthenticated, currentUserFavorite?.userIds]);

  const sidebarItems = [
    {
      icon: <Home className="h-5 w-5" />,
      label: "Dashboard",
      path: "/clients",
      align: "center"
    },
    {
      icon: <Star className="h-5 w-5" />,
      label: "My Favorites",
      path: "/favorites",
      align: "center"
    },
    {
      icon: <FileText className="h-5 w-5" />,
      label: "Find Local Professionals",
      path: "/local-professionals",
      align: "center"
    },
    {
      icon: <Globe className="h-5 w-5" />,
      label: "Find International Professionals",
      path: "/professional-international",
      align: "center"
    },
    {
      icon: <Briefcase className="h-5 w-5" />,
      label: "Post a Job: Local",
      path: "/",
      align: "center"
    },
    {
      icon: <Briefcase className="h-5 w-5" />,
      label: "Post a Job: International",
      path: "/internationalhompage",
      align: "left"
    },
    {
      icon: <User2 className="h-5 w-5" />,
      label: "My Profile",
      path: "/client/profile",
      align: "left"
    },
    {
      icon: <Settings2 className="h-5 w-5" />,
      label: "Settings",
      path: "/client/settings",
      align: "left"
    },
    {
      icon: <MessageCircle className="h-5 w-5" />,
      label: "Messages",
      path: "/client/MessageAndVideoCall",
      align: "left"
    },
    {
      icon: <FileCheck className="h-5 w-5" />,
      label: "My Orders",
      path: "/client/myorders",
      align: "left"
    },
    {
      icon: <MdReviews className="h-5 w-5" />,
      label: "Reviews",
      path: "/client/reviews",
      align: "left"
    },
    {
      icon: <Wallet className="h-5 w-5" />,
      label: "Wallet",
      path: "/client/Wallet",
      align: "left"
    },
    {
      icon: <Shield className="h-5 w-5" />,
      label: "Support",
      path: "/client/support",
      align: "left"
    },
    {
      icon: <LogOut className="h-5 w-5" />,
      label: "Log Out",
      path: "/logout",
      align: "left"
    }
  ];

  const StoreLinks = () => (
    <div className="flex justify-center gap-2 pt-6 px-4">
      <a
        href="https://play.google.com/store/apps/details?id=com.kokobeo"
        className="w-1/2"
      >
        <img
          src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
          alt="Get it on Google Play"
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "120px" }}
        />
      </a>
      <a href="https://apps.apple.com/app/kokobeo" className="w-1/2">
        <img
          src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
          alt="Download on App Store"
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "120px" }}
        />
      </a>
    </div>
  );

  const SocialIcons = () => (
    <SocialMediaLinks className="flex justify-center space-x-4 pt-4 px-4 pb-6" />
  );

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      {/* Fixed Header */}
      <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16 w-full">
        <AdminHeader />
      </div>

      <div className="flex min-h-[calc(100vh-64px)] pt-16">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r overflow-y-auto h-[calc(100vh-64px)]">
          <nav className="p-4 space-y-1 h-full flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {sidebarItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${location.pathname === item.path
                      ? "text-blue-600 bg-blue-50 font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                    } ${item.align === "center" ? "justify-center-important" : ""
                    }`}
                >
                  <span
                    className="flex items-center gap-3"
                    style={
                      item.align === "center"
                        ? { justifyContent: "center !important" }
                        : { justifyContent: "flex-start" }
                    }
                  >
                    {item.icon}
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-auto">
              <StoreLinks />
              <SocialIcons />
            </div>
          </nav>
        </aside>

        {/* Mobile Sidebar */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              />
              <motion.aside
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                className="fixed inset-y-0 left-0 w-full max-w-xs bg-white shadow-xl z-50 lg:hidden overflow-y-auto h-[calc(100vh-64px)]"
              >
                <div className="p-4 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <img
                        src="https://assests.netlify.app/assets/images/logo.png"
                        alt="Logo"
                        className="h-8 w-8"
                      />
                      <span className="text-blue-600 text-lg font-bold">
                        Kokobeo
                      </span>
                    </div>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <nav className="space-y-1 flex-1 overflow-y-auto">
                    {sidebarItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.path}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${location.pathname === item.path
                            ? "text-blue-600 bg-blue-50 font-medium"
                            : "text-gray-600 hover:bg-gray-50"
                          } ${item.align === "center" ? "justify-center-important" : ""
                          }`}
                        style={
                          item.align === "center"
                            ? { justifyContent: "center !important" }
                            : { justifyContent: "flex-start" }
                        }
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="flex items-center gap-3">
                          {item.icon}
                          {item.label}
                        </span>
                      </Link>
                    ))}
                  </nav>
                  <div className="mt-auto">
                    <StoreLinks />
                    <SocialIcons />
                  </div>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="flex-1 w-full lg:pl-64">
          <main className="p-4 lg:p-8 overflow-y-auto h-[calc(100vh-64px)] w-full">
            <div className="max-w-7xl mx-auto space-y-8 w-full">
              {/* Page Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">My Favorites</h1>
                  <p className="text-gray-600 mt-1">
                    Keep track of your preferred professionals and clients
                  </p>
                </div>
                <div className="flex items-center gap-2 lg:hidden">
                  {/* <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setIsMobileMenuOpen(true)}
                  >
                    <Menu className="h-5 w-5 mr-2" />
                    Menu
                  </Button> */}
                </div>
              </div>

              {/* Manual Tabs Container */}
              <Card className="overflow-hidden w-full">
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-gray-600 w-full max-w-md grid grid-cols-2">
                    <button
                      onClick={() => setActiveTab("professionals")}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${activeTab === "professionals"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                      <UserCheck className="h-4 w-4 mr-2" />
                      Favorite Professionals
                    </button>
                    <button
                      onClick={() => setActiveTab("clients")}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${activeTab === "clients"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                      <Briefcase className="h-4 w-4 mr-2" />
                      Favorite Clients
                    </button>
                  </div>
                  <Button onClick={() => navigate("/local-professionals")} variant="outline" size="sm" className="hidden md:flex">
                    <Star className="h-4 w-4 mr-2" />
                    Manage Favorites
                  </Button>
                </div>

                {/* Professionals Tab Content */}
                {activeTab === "professionals" && (
                  <>
                    <div className="p-4 bg-blue-50 flex items-center gap-3">
                      <Search className="h-5 w-5 text-blue-600" />
                      <p className="text-sm text-blue-700">
                        These are professionals you&apos;ve favorited for quick access to their services
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                      {loading || professionalsLoading ? (
                        <div className="col-span-full flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : favoriteProfessionals.length > 0 ? (
                        favoriteProfessionals.map((professional) => (
                          <Card key={professional.id} className="overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <div className="relative">
                                  <img
                                    src={professional.avatar}
                                    alt={professional.name}
                                    className="h-12 w-12 rounded-full object-cover border-2 border-white"
                                  />
                                  {professional.isOnline && (
                                    <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-white"></span>
                                  )}
                                </div>
                                <div>
                                  <h3 className="font-medium text-gray-900">{professional.name}</h3>
                                  <p className="text-sm text-gray-600">{professional.profession}</p>
                                </div>
                              </div>
                              <RealTimeFavoriteButton 
                                user={professional} 
                                currentUserFavorite={currentUserFavorite} 
                              />
                            </div>
                            <div className="p-4">
                              <div className="flex items-center gap-2 mb-3">
                                {professional.reviewsCount > 0 ? (
                                  <button
                                    onClick={() => {
                                      setSelectedProfessionalForReview(professional);
                                      setShowReviewModal(true);
                                    }}
                                    className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                                    title="Click to view all reviews"
                                  >
                                    <StarRatingDisplay
                                      rating={professional.rating}
                                      count={professional.reviewsCount}
                                      size="sm"
                                    />
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <Star className="h-4 w-4 text-gray-300" />
                                    <span className="text-sm text-gray-500">No reviews yet</span>
                                  </div>
                                )}
                                <span className="text-gray-300">|</span>
                                <span className="text-sm text-gray-600">{professional.totalJobs} jobs</span>
                              </div>
                              <p className="text-sm text-gray-700 mb-3 flex items-center">
                                <Globe className="h-4 w-4 mr-1 text-gray-500" />
                                {professional.location}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {professional.tags.map((tag, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="p-4 border-t bg-gray-50 flex justify-between">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/myprofile/${professional.id}`)}
                              >
                                View Profile
                              </Button>
                              <Button size="sm" onClick={() => {
                                navigate("/client/MessageAndVideoCall");
                              }}>Contact</Button>
                            </div>
                          </Card>
                        ))
                      ) : (
                        <div className="col-span-full p-8 text-center">
                          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                            <UserCheck className="h-8 w-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No favorite professionals yet</h3>
                          <p className="text-gray-600 mb-4">Browse professionals and click the star icon to add them to your favorites</p>
                          <div className="flex gap-2 justify-center">
                            <Button onClick={() => navigate("/local-professionals")}>
                              Browse Local Professionals
                            </Button>
                            <Button variant="outline" onClick={() => navigate("/professional-international")}>
                              Browse International Professionals
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Clients Tab Content */}
                {activeTab === "clients" && (
                  <>
                    <div className="p-4 bg-green-50 flex items-center gap-3">
                      <Search className="h-5 w-5 text-green-600" />
                      <p className="text-sm text-green-700">
                        These are clients you&apos;ve worked with and favorited for future opportunities
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                      {loading || clientsLoading ? (
                        <div className="col-span-full flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        </div>
                      ) : favoriteClients.length > 0 ? (
                        favoriteClients.map((client) => (
                          <Card key={client.id} className="overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <div className="relative">
                                  <img
                                    src={client.avatar}
                                    alt={client.name}
                                    className="h-12 w-12 rounded-full object-cover border-2 border-white"
                                  />
                                  {client.isActive && (
                                    <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-white"></span>
                                  )}
                                </div>
                                <div>
                                  <h3 className="font-medium text-gray-900">{client.name}</h3>
                                  <p className="text-sm text-gray-600">{client.industry}</p>
                                </div>
                              </div>
                              <RealTimeFavoriteButton 
                                user={client} 
                                currentUserFavorite={currentUserFavorite} 
                              />
                            </div>
                            <div className="p-4">
                              <div className="flex items-center gap-2 mb-3">
                                {client.reviewsCount > 0 ? (
                                  <button
                                    onClick={() => {
                                      setSelectedClientForReview(client);
                                      setShowReviewModal(true);
                                    }}
                                    className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                                    title="Click to view all reviews"
                                  >
                                    <StarRatingDisplay
                                      rating={client.rating}
                                      count={client.reviewsCount}
                                      size="sm"
                                    />
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <Star className="h-4 w-4 text-gray-300" />
                                    <span className="text-sm text-gray-500">No reviews yet</span>
                                  </div>
                                )}
                                <span className="text-gray-300">|</span>
                                <span className="text-sm text-gray-600">{client.totalProjects} total projects</span>
                              </div>
                              <p className="text-sm text-gray-700 mb-3 flex items-center">
                                <Globe className="h-4 w-4 mr-1 text-gray-500" />
                                {client.location}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {client.projectTypes.map((type, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {type}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="p-4 border-t bg-gray-50 flex justify-between">
                              <Button size="sm" variant="outline">View Profile</Button>
                              <Button size="sm" onClick={() => {
                                navigate("/client/MessageAndVideoCall");
                              }}>Contact</Button>
                            </div>
                          </Card>
                        ))
                      ) : (
                        <div className="col-span-full p-8 text-center">
                          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                            <Briefcase className="h-8 w-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No favorite clients yet</h3>
                          <p className="text-gray-600 mb-4">As you work with clients, you can add them to your favorites for easy access</p>
                          <Button onClick={() => navigate("/local-professionals")}>Browse Jobs</Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Card>
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-white border-t mt-auto w-full">
            <div className="max-w-7xl mx-auto">
              {/* <SharedFooter3 /> */}
            </div>
          </footer>
        </div>
      </div>

      {/* Modals */}
      <PolicyModals
        showPrivacyModal={showPrivacyModal}
        showTermsModal={showTermsModal}
        showProfessionalTermsModal={showProfessionalTermsModal}
        onClosePrivacy={() => setShowPrivacyModal(false)}
        onCloseTerms={() => setShowTermsModal(false)}
        onCloseProfessionalTerms={() => setShowProfessionalTermsModal(false)}
      />

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        professional={selectedProfessionalForReview ? {
          id: selectedProfessionalForReview.id,
          name: selectedProfessionalForReview.name,
          firstName: selectedProfessionalForReview.name.split(" ")[0],
          lastName: selectedProfessionalForReview.name.split(" ").slice(1).join(" "),
          title: selectedProfessionalForReview.profession,
          photo: selectedProfessionalForReview.avatar ? { path: selectedProfessionalForReview.avatar } : undefined,
          averageRating: selectedProfessionalForReview.rating,
          reviewsCount: selectedProfessionalForReview.reviewsCount,
          reviewsList: selectedProfessionalForReview.reviewsList
        } : selectedClientForReview ? {
          id: selectedClientForReview.id,
          name: selectedClientForReview.name,
          firstName: selectedClientForReview.name.split(" ")[0],
          lastName: selectedClientForReview.name.split(" ").slice(1).join(" "),
          title: selectedClientForReview.industry,
          photo: selectedClientForReview.avatar ? { path: selectedClientForReview.avatar } : undefined,
          averageRating: selectedClientForReview.rating,
          reviewsCount: selectedClientForReview.reviewsCount,
          reviewsList: selectedClientForReview.reviewsList
        } : null}
      />
    </div>
  );
};

export default Favorites2;